type TutorialParams = {
  vx: number;
  vy: number;
  spin: number;
  tiltX: number;
};

export class TutorialManager {
  private animationStart: number = 0;
  private active: boolean = false;
  private pointerVisible: boolean = true;
  private currentTilt: number = 0;
  private tiltHandler: ((value: number) => void) | null = null;
  private tutorialParams: TutorialParams = {
    vx: 0,
    vy: -620,
    spin: 0,
    tiltX: 0,
  };
  private launchHandler: ((params: TutorialParams) => void) | null = null;
  private introComplete = false;
  private stage = 0;
  private messageHandler: (text: string, messageData: any) => void = () => {};
  private clearMessagesHandler: (() => void) | null = null;
  private scriptTimers: Array<ReturnType<typeof setTimeout>> = [];
  private interactionHandler: ((blocked: boolean) => void) | null = null;
  private interactionsBlocked = false;
  private profileGuideHandler: (() => Promise<void> | void) | null = null;
  private profileGuideMessageTimer: ReturnType<typeof setTimeout> | null = null;
  private profileGuideHandlerTimer: ReturnType<typeof setTimeout> | null = null;
  private closingSequenceStarted = false;
  private skipPromptVisible = false;

  get isActive(): boolean {
    return this.active;
  }

  setActive(active: boolean) {
    if (active) {
      this.resetScript();
      this.animationStart = 0;
      this.pointerVisible = true;
      this.introComplete = false;
      this.interactionsBlocked = true;
      this.closingSequenceStarted = false;
    } else {
      this.resetScript();
      this.introComplete = false;
      this.interactionsBlocked = false;
      this.closingSequenceStarted = false;
    }
    this.active = active;
    this.updateInteractionState();
  }

  private resetScript() {
    this.stage = 0;
    this.clearScriptTimers();
    this.clearProfileGuideTimers();
    this.closingSequenceStarted = false;
    this.skipPromptVisible = false;
  }

  showPointer() {
    this.pointerVisible = true;
  }

  hidePointer() {
    this.pointerVisible = false;
  }

  setTilt(tilt: number) {
    this.currentTilt = tilt;
    this.tiltHandler?.(tilt);
  }

  setTiltHandler(handler: (value: number) => void) {
    this.tiltHandler = handler;
    this.tiltHandler?.(this.currentTilt);
  }

  applyTilt() {
    this.tiltHandler?.(this.currentTilt);
  }

  drawPointer(ctx: CanvasRenderingContext2D, ballX: number, ballY: number, ballR: number, currentTime: number) {
    if (!this.active || !this.pointerVisible) return;

    if (this.animationStart === 0) {
      this.animationStart = currentTime;
    }

    ctx.save();

    const animationDuration = 1500;
    const animationOffset = 80;
    const elapsed = currentTime - this.animationStart;
    const progress = (elapsed % animationDuration) / animationDuration;

    const easeInOut = (t: number) => {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    const yOffset = -animationOffset * easeInOut(progress);

    const pointerX = ballX + ballR;
    const pointerY = ballY + ballR + 15 + yOffset;

    ctx.font = '48px system-ui, -apple-system, Segoe UI, Roboto';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(255, 200, 0, 0.5)';
    ctx.shadowBlur = 10;
    ctx.fillText('👆', pointerX, pointerY);

    ctx.restore();
  }

  setMessageHandler(handler: (text: string, messageData: any) => void) {
    this.messageHandler = handler;
  }

  setClearMessagesHandler(handler: () => void) {
    this.clearMessagesHandler = handler;
  }

  setProfileGuideHandler(handler: () => Promise<void> | void) {
    this.profileGuideHandler = handler;
  }

  private showMessage(text: string, messageData: any) {
    this.messageHandler(text, messageData);
  }

  private hideSkipPromptIfVisible() {
    if (!this.skipPromptVisible) return;
    this.clearMessagesHandler?.();
    this.skipPromptVisible = false;
  }

  private clearScriptTimers() {
    this.scriptTimers.forEach(timer => clearTimeout(timer));
    this.scriptTimers = [];
  }

  private schedule(fn: () => void, delay: number) {
    const timer = setTimeout(() => {
      fn();
      this.scriptTimers = this.scriptTimers.filter(entry => entry !== timer);
    }, delay);
    this.scriptTimers.push(timer);
  }

  private clearProfileGuideTimers() {
    if (this.profileGuideMessageTimer) {
      clearTimeout(this.profileGuideMessageTimer);
      this.profileGuideMessageTimer = null;
    }
    if (this.profileGuideHandlerTimer) {
      clearTimeout(this.profileGuideHandlerTimer);
      this.profileGuideHandlerTimer = null;
    }
  }

  private delay(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
  }

  private async playClosingMessages() {
    if (this.closingSequenceStarted) return;
    this.closingSequenceStarted = true;
    this.stage = -1;
    await this.delay(500);
    const closingMessages = [
      { text: 'Take it from here. Defeat Robo Roller!', startX: 0.5, startY: 0.42 },
      { text: 'One more thing...', startX: 0.5, startY: 0.34 },
      { text: 'If someone makes 21, the game\'s not over!', startX: 0.5, startY: 0.36 },
      { text: 'There can be retribution!!!', startX: 0.5, startY: 0.38 },
      { text: 'Good luck, and happy Skrolling!', startX: 0.5, startY: 0.4 },
    ];
    for (const msg of closingMessages) {
      this.showMessage(msg.text, {
        type: 'roll-feedback',
        text: [msg.text],
        positioning: {
          mode: 'fixed',
          startX: msg.startX,
          startY: msg.startY,
          duration: 2700,
          fontSize: 32,
          color: 'lime',
          effect: 'pulse',
          lineShrink: 0,
          linePerspectiveShrink: 0
        }
      });
      await this.delay(2800);
    }
    this.setActive(false);
  }

  notifyAITurnStarted() {
    if (this.stage !== 99 || this.closingSequenceStarted) return;
    this.hideSkipPromptIfVisible();
    void this.playClosingMessages();
  }

  async handleSink(pts: number) {
    console.log('[tutorial]: ball just sank. tutorial stage is: ' + this.stage);
    if (!this.active) return;
    this.clearScriptTimers();
    console.log('[tutorial] stage: ' + this.stage);
    if (this.stage === 6) {
      await this.delay(500);
      this.showMessage('But if you go over 21, you bust! Back to 15!', {
        type: 'roll-feedback',
        text: ['But if you go over 21, you bust!', 'Back to 15!'],
        positioning: {
          mode: 'fixed',
          startX: 0.5,
          startY: 0.6,
          duration: 1800,
          fontSize: 34,
          color: 'white',
          effect: 'glow',
          linePerspectiveShrink: 0,
          delay: 1800
        }
      });
      await this.delay(800);
      this.setTutorialParams({ vx: -43, vy: -410 });
      this.setTilt(16);
      this.launchHandler?.(this.tutorialParams);
      this.stage = 7;
      return;
    }
    if (this.stage === 7) {
      await this.delay(1000);
      this.setTutorialParams({ vx: -32, vy: -150 });
      this.setTilt(4);
      this.launchHandler?.(this.tutorialParams);
      await this.delay(2000);
      this.showMessage('Skip to the next ball with that button', {
        type: 'roll-feedback',
        text: ['Skip to the next ball with that button \n ↙️'],
        positioning: {
          mode: 'fixed',
          startX: 0.38,
          startY: 0.8,
          duration: 9000,
          fontSize: 40,
          color: 'lime',
          effect: 'pulse',
          linePerspectiveShrink: 0
        }
      });
      this.setActive(false);
      this.skipPromptVisible = true;
      this.stage = 99;
      return;
    }

    if (this.stage === 5) {      
      await this.delay(500);
      this.setTutorialParams({ vx: -55, vy: -340 });
      this.setTilt(26);
      console.log('launching curve from left to right now');
      this.launchHandler?.(this.tutorialParams);
      await this.delay(700);
      this.showMessage('so mind which end of the table you roll from', {
        type: 'roll-feedback',
        text: ['so mind which end of the table you roll from'],
        positioning: {
          mode: 'fixed',
          startX: 0.62,
          startY: 0.64,
          duration: 2500,
          fontSize: 42,
          color: 'green',
          effect: 'glow',
          linePerspectiveShrink: 0          
        }
      });
      this.stage = 6;
      console.log('stage: ' + this.stage);
      return;
    }
    if (this.stage === 0) {
      this.showMessage('5 POINTS!', {
        type: 'roll-feedback',
        text: ['5 POINTS!'],
        positioning: {
          mode: 'fixed',
          startX: 0.52,
          startY: 0.42,
          duration: 1500,
          fontSize: 34,
          color: 'white',
          effect: 'glow'
        }
      });
      this.schedule(() => {
        this.showMessage('Your score is here', {
          type: 'roll-feedback',
          text: ['Your score is here \n       ⬅️'],
          positioning: {
            mode: 'fixed',
            startX: 0.43,
            startY: 0.60,
            duration: 1500,
            fontSize: 40,
            color: 'lime',
            effect: 'pulse'
          }
        });
      }, 2000);

      this.schedule(() => {
        this.setTutorialParams({ vx: 35 });        

        this.launchHandler?.(this.tutorialParams);
      }, 1500);

      this.stage = 1;
      console.log('[tutorial] stage: ' + this.stage);
    } else if (this.stage === 1) {
      this.showMessage('3 more points! See how the holes light up?', {
        type: 'multi-part',
        text: ['3 more points!', 'See how the holes light up?'],
        positioning: {
          mode: 'fixed',
          startX: 0.52,
          startY: 0.42,
          duration: 2000,
          fontSize: 28,
          color: 'white',
          effect: 'glow',
          delay: 2100
        }
      });
      this.schedule(() => {
        this.setTutorialParams({ vx: -35, vy: -325 });
        this.launchHandler?.(this.tutorialParams);
      }, 1300);
      this.stage = 2;
      console.log('[tutorial] stage: ' + this.stage);
    } else if (this.stage === 2) {
      await this.delay(1000);
      this.showMessage('Get each score', {
        type: 'multi-part',
        text: ['Sink each hole', 'in the same turn', 'plus any other hole'],
        positioning: {
          mode: 'fixed',
          startX: 0.52,
          startY: 0.42,
          duration: 1400,
          fontSize: 28,
          color: 'white',
          effect: 'glow',
          delay: 1500
        }
      });

      await this.delay(1500);
      this.setTutorialParams({ vx: -23, vy: -289 });
      this.launchHandler?.(this.tutorialParams);
      await this.delay(3000);
      this.showMessage('your score is set to 21!', {
        type: 'roll-feedback',
        text: ['your score is set to 21!'],
        positioning: {
          mode: 'fixed',
          startX: 0.52,
          startY: 0.42,
          duration: 1400,
          fontSize: 38,
          color: 'lime',
          effect: 'pulse'
        }
      });
      
      this.stage = 3;
      console.log('[tutorial] stage: ' + this.stage);
    }
    this.updateInteractionState();
  }

  async handleDitch() {
    if (!this.active || this.stage !== 3) return;
    await this.delay(250);
    this.showMessage('So close!', {
      type: 'roll-feedback',
      text: ['So close!', 'The ditch is 0 points :('],
      positioning: {
        mode: 'fixed',
        startX: 0.52,
        startY: 0.42,
        duration: 1600,
        fontSize: 32,
        color: 'white',
        effect: 'glow',
        delay: 1700
      }
    });
    this.stage = 4;
    this.updateInteractionState();
    this.clearProfileGuideTimers();
    this.profileGuideMessageTimer = setTimeout(() => {
      this.showMessage('<-- Your profile', {
        type: 'roll-feedback',
        text: ['⬅️ Your profile'],
        positioning: {
          mode: 'fixed',
          startX: 0.21,
          startY: 0.03,
          duration: 7000,
          fontSize: 36,
          color: 'red',
          effect: 'pulse'
        }
      });
    }, 2000);
    this.profileGuideHandlerTimer = setTimeout(() => {
      console.log('call the profile show now: ');
      this.profileGuideHandler?.();
    }, 6000);
    console.log('just scheduled the profile show');
  }

  startCurveSequence() {
    if (!this.active || this.stage !== 4) return;
    this.stage = 5;
    this.setTutorialParams({ vx: 55, vy: -340 });
    this.setTilt(-26);
    this.showMessage('the board has a bit of a curve', {
      type: 'roll-feedback',
      text: ['the board has a bit of a curve'],
      positioning: {
        mode: 'fixed',
        startX: 0.45,
        startY: 0.78,
        duration: 2500,
        fontSize: 26,
        color: 'white',
        effect: 'glow',
        linePerspectiveShrink: 0        
      }
    });
    this.launchHandler?.(this.tutorialParams);
  }

  setLaunchHandler(handler: (params: TutorialParams) => void) {
    this.launchHandler = handler;
  }

  setTutorialParams(params: Partial<TutorialParams>) {
    this.tutorialParams = { ...this.tutorialParams, ...params };
  }

  onIntroComplete() {
    if (this.introComplete) return;
    this.introComplete = true;
    this.hidePointer();
    this.applyTilt();
    this.launchHandler?.(this.tutorialParams);
  }

  setInteractionHandler(handler: (blocked: boolean) => void) {
    this.interactionHandler = handler;
    this.updateInteractionState();
  }

  setInteractionBlocked(blocked: boolean) {
    this.interactionsBlocked = blocked;
    this.updateInteractionState();
  }

  shouldBlockInteractions(): boolean {
    return this.interactionsBlocked;
  }

  private updateInteractionState() {
    this.interactionHandler?.(this.shouldBlockInteractions());
  }
}
